import {
  CompanySizeReadable,
  companySizesCreateMutation,
  companySizesDestroyMutation,
  companySizesListOptions,
  companySizesListQueryKey,
  companySizesPartialUpdateMutation,
  companySizesRetrieveOptions
} from '@crm/api'
import { ConfirmModal } from '@crm/components'
import {
  FormModal,
  FormNomenclatureDefaultItem
} from '@crm/features/nomenclatures/components'
import { useTableSorter } from '@crm/hooks/useTableSorter'
import { defaultFilters } from '@crm/utils'
import { DayLabel, TableActions, TableContainer } from '@shared/components'
import {
  useInvalidatePredicateQueries,
  useQueryParams,
  useResetPagination
} from '@shared/hooks'
import { useLocales } from '@shared/locales'
import { showApiErrors } from '@shared/utils'
import { useMutation, useQuery } from '@tanstack/react-query'
import { App, Button } from 'antd'
import { Add, Edit, Trash } from 'iconsax-react'
import { useCallback, useMemo, useState } from 'react'

import type { MenuProps, TableColumnsType } from 'antd'

const formName = 'sizes'

export const NomenclaturesSizes = () => {
  const { t } = useLocales()
  const params = useQueryParams()

  const { invalidateQueries } = useInvalidatePredicateQueries()
  const { resetPagination } = useResetPagination()

  const { notification } = App.useApp()

  const sorter = useTableSorter()

  const [isCreate, setIsCreate] = useState(false)
  const [editItem, setEditItem] = useState<number>()
  const [deleteItem, setDeleteItem] = useState<number>()

  const { data: sizes, isLoading } = useQuery(
    companySizesListOptions({
      query: {
        ...defaultFilters,
        ...params
      }
    })
  )

  const { mutate } = useMutation({
    ...companySizesDestroyMutation(),
    onError: (e) => showApiErrors(e),
    onSuccess: () => {
      invalidateQueries([companySizesListQueryKey()])
      resetPagination()
      setDeleteItem(undefined)
      notification.success({
        message: t('success.success'),
        description: t('success.success_data_delete')
      })
    }
  })

  const onDelete = () => {
    if (deleteItem) {
      mutate({ path: { id: deleteItem } })
    }
  }

  const onCancelDelete = () => setDeleteItem(undefined)

  const onCloseModal = () => {
    setEditItem(undefined)
    setIsCreate(false)
  }

  const getActionColumnItem: (id: number) => MenuProps['items'] = useCallback(
    (id) => [
      {
        label: t('nomenclature.edit'),
        key: 'edit',
        icon: <Edit size={20} />,
        onClick: () => setEditItem(id)
      },
      { type: 'divider' },
      {
        label: t('nomenclature.delete'),
        key: 'delete',
        icon: <Trash size={20} />,
        danger: true,
        onClick: () => setDeleteItem(id)
      }
    ],
    [t]
  )

  const columns: TableColumnsType<CompanySizeReadable> = useMemo(
    () => [
      {
        title: t('nomenclature.name'),
        dataIndex: 'name',
        key: 'name',
        sorter: true
      },
      {
        title: t('filter.created_at'),
        dataIndex: 'created_at',
        key: 'created_at',
        sorter: true,
        render: (created_at) => <DayLabel date={created_at} />
      },
      {
        title: t('lead.modified_at'),
        dataIndex: 'modified_at',
        key: 'modified_at',
        sorter: true,
        render: (modified_at) => <DayLabel date={modified_at} />
      },
      {
        width: 50,
        fixed: 'right',
        render: ({ id }) => <TableActions options={getActionColumnItem(id)} />
      }
    ],
    [t, getActionColumnItem]
  )

  return (
    <>
      <TableContainer
        data={sizes}
        columns={columns}
        loading={isLoading}
        rightChildren={
          <Button
            type="primary"
            onClick={() => setIsCreate(true)}
            icon={<Add size={20} />}
          >
            {t('nomenclature.add_size')}
          </Button>
        }
        onChange={(_, __, result) => sorter(result)}
        bordered
      />

      <ConfirmModal
        open={!!deleteItem}
        onSuccess={onDelete}
        onCancel={onCancelDelete}
      />

      <FormModal
        id={editItem}
        formName={formName}
        open={!!(editItem || isCreate)}
        getItem={companySizesRetrieveOptions}
        onCreate={companySizesCreateMutation}
        onUpdate={companySizesPartialUpdateMutation}
        onCloseModal={onCloseModal}
      >
        <FormNomenclatureDefaultItem />
      </FormModal>
    </>
  )
}
